import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-[5px] text-sm font-[500] ring-offset-white transition-all duration-150 gap-2 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:-translate-y-px",
  {
    variants: {
      variant: {
        default:
          "text-white bg-gradient-to-b from-blue-500 to-blue-600 border-2 border-slate-800 shadow-[4px_4px_0px_0px_#0f172a] hover:brightness-110 active:translate-x-[4px] active:translate-y-[4px] active:shadow-none",
        noShadow: "text-white bg-gradient-to-b from-blue-500 to-blue-600 border-2 border-slate-800",
        neutral:
          "bg-[#0d1013] text-slate-300 border border-slate-700 shadow-[2px_2px_0px_0px_white] hover:bg-slate-800 active:translate-x-[2px] active:translate-y-[2px] active:shadow-none",
        reverse:
          "text-white bg-gradient-to-b from-blue-500 to-blue-600 border-2 border-slate-800 hover:brightness-110 active:translate-x-[-4px] active:translate-y-[-4px] active:shadow-[4px_4px_0px_0px_#0f172a]",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = "Button"

export { Button, buttonVariants }